import _ from 'lodash'

/**
 * Provides the list of active capabilities and a grouping utility.
 *
 * @type {ngService}
 */
export class TmrCapabilities {
  static ngType = 'service'

  constructor($translate, tmrFrontendCapabilities, Role) {
    'ngInject'
    Object.assign(this, { $translate, tmrFrontendCapabilities, Role })
  }

  /**
   * Gets active capabilities.
   *
   * @returns {Promise<array>} A promise of active capabilities.
   */
  get() {
    return this.Role.capabilities().$promise.then(capabilities =>
      this.tmrFrontendCapabilities.concat(capabilities)
    )
  }

  /**
   * Groups a list of capabilities.
   *
   * Dots in capability names determine the group.
   * E.g.: The capability Foo.Bar.Baz is part of the Foo.Bar group
   *
   * @returns {object} A map of groups.
   */
  group(capabilities) {
    let groups = {}
    let groupedByLength = _.groupBy(
      capabilities.map(capability => capability.split('.')),
      parts => parts.length
    )

    // Capabilities with 2 parts are groups.
    if (groupedByLength[2] !== undefined) {
      groupedByLength[2].forEach(splitted => {
        let capability = splitted.join('.')
        groups[capability] = {
          label: 'CAPABILITY.' + capability.toUpperCase(),
          capabilities: [],
        }
      })
    }

    // Capabilities with 3 parts are part of a group.
    if (groupedByLength[3] !== undefined) {
      groupedByLength[3].forEach(splitted => {
        let group = splitted[0] + '.' + splitted[2]
        // The group name is the first and the last part.
        // E.g.: the group of Resource.User.edit is Resource.edit.
        if (groups[group] !== undefined) {
          let capability = splitted.join('.')
          let label = 'CAPABILITY.' + capability.toUpperCase()
          let shortLabel = 'CAPABILITY.SHORTLABEL.' + capability.toUpperCase()
          groups[group].capabilities.push({
            capability: capability,
            label:
              this.$translate.instant(label) === label ? capability : label,
            shortLabel:
              this.$translate.instant(shortLabel) === shortLabel
                ? capability
                : shortLabel,
          })
        }
        // There is no group: move to ungrouped.
        else {
          groupedByLength[1].push([splitted.join('.')])
        }
      })
    }

    // Capabilities with 1 part are ungrouped.
    if (groupedByLength[1] !== undefined) {
      groups['ungrouped'] = {
        label: 'CAPABILITY.OTHER',
        capabilities: groupedByLength[1].map(splitted => ({
          capability: splitted[0],
          shortLabel: splitted[0],
        })),
      }
    }

    return groups
  }

  /**
   * Creates a nested object of capabilities models, suitable for use by ng-checkbox.
   *
   * @param {object} groups The return value of TmrCapabilities#groups
   * @param {array} defaults A list of active capabilities
   * @returns {object}
   */
  groupModels(groups, defaults) {
    let models = {}
    Object.keys(groups).forEach(group => {
      if (group !== 'ungrouped') {
        models[group] = defaults.indexOf(group) > -1
      }
      groups[group].capabilities.forEach(
        capability =>
          (models[capability.capability] =
            defaults.indexOf(capability.capability) > -1 || models[group])
      )
    })
    return models
  }
}
